import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Mail, Calculator, Send, RefreshCw, Filter, X, FileText, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  quote_requested: "Forespørgsel modtaget",
  under_review: "Under behandling",
  offer_prepared: "Tilbud klargjort",
  offer_sent: "Tilbud sendt",
  agreement_sent: "Aftale sendt",
  signed: "Underskrevet",
  active_customer: "Aktiv kunde",
  rejected: "Afvist",
  expired: "Udløbet",
  outside_current_service_area: "Udenfor område (future expansion)",
};

const STATUS_COLORS = {
  quote_requested: "bg-blue-100 text-blue-800",
  under_review: "bg-amber-100 text-amber-800",
  offer_prepared: "bg-purple-100 text-purple-800",
  offer_sent: "bg-indigo-100 text-indigo-800",
  agreement_sent: "bg-fuchsia-100 text-fuchsia-800",
  signed: "bg-emerald-100 text-emerald-800",
  active_customer: "bg-emerald-200 text-emerald-900",
  rejected: "bg-rose-100 text-rose-800",
  expired: "bg-stone-100 text-stone-700",
  outside_current_service_area: "bg-orange-100 text-orange-800",
};

const fmt = (n) => Number(n || 0).toLocaleString("da-DK");

export default function QuoteRequestsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", region: "", q: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set("status", filter.status);
      if (filter.region) params.set("region", filter.region);
      if (filter.q) params.set("q", filter.q);
      const { data } = await axios.get(`${API}/api/admin/quote-requests?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.items || []);
      setSummary(data.summary || {});
    } finally {
      setLoading(false);
    }
  }, [token, filter.status, filter.region, filter.q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="quote-requests-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-brand-dark">Tilbud / Forespørgsler</h2>
          <p className="text-sm text-brand-text2 mt-1">Public quote-flow → intern prisberegning → tilbud → aftale-signering</p>
        </div>
        <button onClick={load} className="text-sm flex items-center gap-2 border border-stone-300 px-3 py-2 hover:bg-stone-50" data-testid="quote-refresh">
          <RefreshCw size={14} /> Opdater
        </button>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs" data-testid="quote-summary">
        {Object.entries(STATUS_LABELS).slice(0, 10).map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter((f) => ({ ...f, status: f.status === k ? "" : k }))}
            className={`p-3 border text-left ${filter.status === k ? "border-brand-red bg-brand-red/5" : "border-stone-200"}`}
            data-testid={`quote-summary-${k}`}>
            <div className="font-bold text-brand-dark">{summary[k] || 0}</div>
            <div className="text-brand-text2 mt-0.5 leading-tight">{lbl}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-stone-50 p-3 border border-stone-200">
        <Filter size={14} className="text-stone-500" />
        <input placeholder="Søg firma/CVR/email/ref..." value={filter.q}
          onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
          className="px-3 py-2 text-sm border border-stone-300 flex-1 min-w-48" data-testid="quote-search" />
        <select value={filter.region} onChange={(e) => setFilter((f) => ({ ...f, region: e.target.value }))}
          className="px-3 py-2 text-sm border border-stone-300" data-testid="quote-filter-region">
          <option value="">Alle regioner</option>
          <option value="Sjælland">Sjælland</option>
          <option value="Fyn">Fyn</option>
          <option value="Jylland">Jylland</option>
          <option value="Bornholm">Bornholm</option>
          <option value="Andet">Andet</option>
        </select>
      </div>

      {/* Table */}
      <div className="border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="quote-table">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-600">
            <tr>
              <th className="p-3">Reference</th>
              <th className="p-3">Firma</th>
              <th className="p-3">Region</th>
              <th className="p-3">Pakker/md</th>
              <th className="p-3">Service</th>
              <th className="p-3">Status</th>
              <th className="p-3">Modtaget</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-stone-500">Indlæser...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-stone-500">Ingen forespørgsler endnu</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-stone-200 hover:bg-stone-50">
                <td className="p-3 font-mono text-xs">{it.reference}</td>
                <td className="p-3">
                  <div className="font-semibold text-brand-dark">{it.company_name}</div>
                  <div className="text-xs text-stone-500">CVR {it.cvr}</div>
                </td>
                <td className="p-3">{it.pickup_region}</td>
                <td className="p-3">{fmt(it.expected_monthly_packages)}</td>
                <td className="p-3 text-xs">{it.service_need}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 ${STATUS_COLORS[it.status] || "bg-stone-100"}`}>{STATUS_LABELS[it.status] || it.status}</span>
                </td>
                <td className="p-3 text-xs text-stone-500">{(it.created_at_iso || "").slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setSelected(it.id)} className="text-brand-red text-xs underline" data-testid={`quote-open-${it.reference}`}>Åbn</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <QuoteDetailModal id={selected} token={token} onClose={() => setSelected(null)} onChange={load} />}
    </div>
  );
}

function QuoteDetailModal({ id, token, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingFields, setSavingFields] = useState({});
  const [offerForm, setOfferForm] = useState({ price_per_pkg_dkk: "", monthly_volume: "", cover_message: "" });
  const [transition, setTransition] = useState({ status: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data: q } = await axios.get(`${API}/api/admin/quote-requests/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(q);
      setOfferForm({
        price_per_pkg_dkk: q.agreed_price_per_pkg_dkk || q.internal_price_suggestion?.price_per_pkg_dkk || "",
        monthly_volume: q.agreed_monthly_volume || q.expected_monthly_packages || "",
        cover_message: "",
      });
      const { data: a } = await axios.get(`${API}/api/admin/quote-requests/${id}/audit`, { headers: { Authorization: `Bearer ${token}` } });
      setAudit(a.items || []);
    } finally { setLoading(false); }
  }, [id, token]);

  useEffect(() => { reload(); }, [reload]);

  const patchFields = async (fields) => {
    setBusy(true);
    try {
      const { data: r } = await axios.patch(
        `${API}/api/admin/quote-requests/${id}`,
        fields,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Merge updated quote locally instead of triggering a full reload+loader.
      // This prevents the "Indlæser..." flicker on every keystroke/click.
      if (r?.quote) setData(r.quote);
      else setData((prev) => ({ ...prev, ...fields }));
      onChange?.();
      setSavingFields({});
    } finally { setBusy(false); }
  };

  const calcPrice = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/api/admin/quote-requests/${id}/calculate-price`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert(`Internt prisforslag: ${r.price_per_pkg_dkk} DKK/pkg (${r.tier_range})${r.requires_green_profitability ? " — kræver grøn profitabilitet" : ""}`);
      await reload();
    } finally { setBusy(false); }
  };

  const sendOffer = async () => {
    if (!offerForm.price_per_pkg_dkk || !offerForm.monthly_volume) return alert("Pris og volumen skal udfyldes");
    if (!window.confirm(`Send tilbud til ${data?.contact_email}?\n\nPris: ${offerForm.price_per_pkg_dkk} DKK/pkg\nVolumen: ${offerForm.monthly_volume} pakker/md`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/quote-requests/${id}/send-offer`, {
        price_per_pkg_dkk: parseFloat(offerForm.price_per_pkg_dkk),
        monthly_volume: parseInt(offerForm.monthly_volume, 10),
        cover_message: offerForm.cover_message || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await reload(); onChange?.();
      alert("Tilbud sendt ✅");
    } catch (e) {
      alert("Fejl: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const changeStatus = async () => {
    if (!transition.status) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/api/admin/quote-requests/${id}/status`, transition, { headers: { Authorization: `Bearer ${token}` } });
      setTransition({ status: "", reason: "" });
      await reload(); onChange?.();
    } catch (e) {
      alert("Fejl: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (loading || !data) return <Modal onClose={onClose}><div className="p-8 text-center">Indlæser...</div></Modal>;

  const sugg = data.internal_price_suggestion;
  return (
    <Modal onClose={onClose}>
      <div className="p-6" data-testid="quote-detail-modal">
        <div className="flex items-start justify-between gap-3 border-b border-stone-200 pb-4 mb-5">
          <div>
            <div className="text-xs font-mono text-stone-500">{data.reference}</div>
            <h3 className="text-2xl font-bold text-brand-dark">{data.company_name}</h3>
            <div className="text-sm text-stone-500">CVR {data.cvr}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-1 ${STATUS_COLORS[data.status] || "bg-stone-100"}`}>{STATUS_LABELS[data.status]}</span>
            <button onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {/* Internal price suggestion */}
        <div className="bg-amber-50 border border-amber-300 p-4 mb-5">
          <div className="flex items-center gap-2 text-sm font-bold text-amber-900 mb-2">
            <Calculator size={16} /> Intern prisanbefaling (vises IKKE til kunde)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-xs text-stone-500">Tier-anbefaling</div><div className="font-semibold">{sugg?.price_per_pkg_dkk} DKK/pkg</div></div>
            <div><div className="text-xs text-stone-500">Tier-range</div><div className="font-semibold text-xs">{sugg?.tier_range}</div></div>
            <div><div className="text-xs text-stone-500">Forventet volumen</div><div className="font-semibold">{fmt(data.expected_monthly_packages)} pakker/md</div></div>
            <div><div className="text-xs text-stone-500">Pickup/dag</div><div className="font-semibold">{fmt(data.expected_packages_per_pickup_day)}</div></div>
          </div>
          {sugg?.requires_green_profitability && (
            <div className="mt-2 text-xs text-amber-900 font-semibold">⚠️ Denne tier kræver verificeret grøn profitabilitet før den må tilbydes</div>
          )}
          <button onClick={calcPrice} disabled={busy}
            className="mt-3 text-xs bg-amber-700 text-white px-3 py-1.5 disabled:opacity-50" data-testid="quote-calc-price">
            Genberegn prisforslag
          </button>
        </div>

        {/* Customer data grid */}
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm mb-6">
          <Row k="Kontaktperson" v={`${data.contact_name} · ${data.contact_email} · ${data.contact_phone}`} />
          <Row k="Faktura-email" v={data.billing_email} />
          <Row k="Afhentning" v={data.pickup_address} />
          <Row k="Region" v={`${data.pickup_region} ${data.is_active_area ? "(aktivt område)" : "(future expansion)"}`} />
          <Row k="Afhentningsfrekvens" v={`${data.pickup_frequency} · ${data.preferred_pickup_time}`} />
          <Row k="Service" v={data.service_need} />
          <Row k="Leveringsområde" v={data.delivery_area} />
          <Row k="Vægt (gns/max)" v={`${data.average_package_weight_kg} kg / ${data.max_package_weight_kg} kg`} />
          <Row k="Pakke-noter" v={data.package_size_notes || "—"} />
          <Row k="Returer" v={data.needs_returns ? "Ja" : "Nej"} />
          <Row k="Modtaget" v={data.created_at_iso?.slice(0, 16).replace("T", " ")} />
          {data.comments && <div className="md:col-span-2"><Row k="Kommentarer" v={data.comments} /></div>}
        </div>

        {/* Internal fields */}
        <div className="bg-stone-50 p-4 border border-stone-200 mb-5">
          <div className="text-sm font-bold text-brand-dark mb-3">Interne felter</div>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={data.pickup_address_approved}
                onChange={(e) => patchFields({ pickup_address_approved: e.target.checked })}
                data-testid="quote-pickup-approved" />
              <span>Afhentningsadresse godkendt</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={data.vehicle_insurance_business_approved}
                onChange={(e) => patchFields({ vehicle_insurance_business_approved: e.target.checked })}
                data-testid="quote-insurance-approved" />
              <span>Køretøjsforsikring (erhverv) bekræftet</span>
            </label>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold text-stone-600 mb-1">Interne noter</label>
            <textarea value={savingFields.internal_notes ?? data.internal_notes ?? ""}
              onChange={(e) => setSavingFields((s) => ({ ...s, internal_notes: e.target.value }))}
              onBlur={(e) => e.target.value !== (data.internal_notes || "") && patchFields({ internal_notes: e.target.value })}
              rows={3} className="w-full text-sm border border-stone-300 p-2" data-testid="quote-notes" />
          </div>
        </div>

        {/* Sprint 2R-6E — Payment method & terms (admin only) */}
        <PaymentMethodBlock data={data} patchFields={patchFields} busy={busy} />

        {/* Offer block */}
        <div className="bg-indigo-50 border border-indigo-300 p-4 mb-5">
          <div className="flex items-center gap-2 text-sm font-bold text-indigo-900 mb-3">
            <Send size={16} /> Forbered &amp; send tilbud
          </div>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <div>
              <label className="block text-xs text-stone-600 mb-1">Pris pr. pakke (DKK, ex moms)</label>
              <input type="number" step="0.01" value={offerForm.price_per_pkg_dkk}
                onChange={(e) => setOfferForm({ ...offerForm, price_per_pkg_dkk: e.target.value })}
                className="w-full border border-stone-300 p-2" data-testid="quote-offer-price" />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Aftalt månedlig volumen</label>
              <input type="number" value={offerForm.monthly_volume}
                onChange={(e) => setOfferForm({ ...offerForm, monthly_volume: e.target.value })}
                className="w-full border border-stone-300 p-2" data-testid="quote-offer-volume" />
            </div>
            <div className="flex items-end">
              <button onClick={sendOffer} disabled={busy || !offerForm.price_per_pkg_dkk}
                className="w-full bg-indigo-700 text-white px-4 py-2 disabled:opacity-50" data-testid="quote-send-offer">
                Send tilbud til kunde
              </button>
            </div>
          </div>
          <textarea value={offerForm.cover_message}
            onChange={(e) => setOfferForm({ ...offerForm, cover_message: e.target.value })}
            placeholder="Valgfri besked til kunden i tilbuddet..." rows={2}
            className="w-full mt-2 text-sm border border-stone-300 p-2" data-testid="quote-cover-message" />
          {data.offer_sent_at_iso && (
            <div className="mt-2 text-xs text-indigo-900">
              ✓ Tilbud sendt {data.offer_sent_at_iso.slice(0, 10)} · gyldig til {(data.offer_valid_until_iso || "").slice(0, 10)}
            </div>
          )}
        </div>

        {/* Status transition */}
        <div className="bg-white border border-stone-200 p-4 mb-5">
          <div className="text-sm font-bold text-brand-dark mb-2">Skift status (audit-logged)</div>
          <div className="flex flex-wrap gap-2 items-center">
            <select value={transition.status} onChange={(e) => setTransition({ ...transition, status: e.target.value })}
              className="text-sm border border-stone-300 p-2" data-testid="quote-status-select">
              <option value="">— Vælg ny status —</option>
              {(data.allowed_transitions || []).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
              ))}
            </select>
            <input value={transition.reason}
              onChange={(e) => setTransition({ ...transition, reason: e.target.value })}
              placeholder="Begrundelse (valgfri)" className="text-sm border border-stone-300 p-2 flex-1 min-w-48"
              data-testid="quote-status-reason" />
            <button onClick={changeStatus} disabled={busy || !transition.status}
              className="bg-brand-dark text-white text-sm px-3 py-2 disabled:opacity-50" data-testid="quote-status-apply">
              Skift status
            </button>
          </div>
        </div>

        {/* Audit log */}
        <details className="text-sm" data-testid="quote-audit">
          <summary className="font-semibold cursor-pointer text-brand-dark">Audit-log ({audit.length})</summary>
          <div className="mt-3 space-y-2">
            {audit.map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-stone-300 pl-3 py-1">
                <div className="font-semibold">{a.action} <span className="text-stone-500 font-normal">— {a.actor_email}</span></div>
                <div className="text-stone-500">{a.created_at_iso?.replace("T", " ").slice(0, 19)}</div>
                {Object.keys(a.payload || {}).length > 0 && (
                  <pre className="mt-1 text-[10px] bg-stone-50 p-2 border border-stone-200 whitespace-pre-wrap">{JSON.stringify(a.payload, null, 2)}</pre>
                )}
              </div>
            ))}
          </div>
        </details>
      </div>
    </Modal>
  );
}

const Row = ({ k, v }) => (
  <div>
    <div className="text-xs text-stone-500">{k}</div>
    <div className="text-brand-dark">{v}</div>
  </div>
);

// Sprint 2R-6E — Payment method block (admin only)
const PAYMENT_METHOD_OPTIONS = [
  { value: "billy_invoice",        label: "Faktura (Billy, månedlig)" },
  { value: "weekly_invoice",       label: "Ugentlig fakturering" },
  { value: "prepaid_pilot",        label: "Forudbetalt pilot" },
  { value: "automatic_collection", label: "Automatisk opkrævning (mandat)" },
];
const INVOICE_FREQ_OPTIONS = [
  { value: "monthly",     label: "Månedlig" },
  { value: "weekly",      label: "Ugentlig" },
  { value: "on_delivery", label: "Ved levering" },
];
const AUTO_COLLECT_STATUSES = [
  { value: "not_enabled",        label: "Ikke aktiveret" },
  { value: "requested",          label: "Anmodet" },
  { value: "sent_to_customer",   label: "Sendt til kunde" },
  { value: "signed_by_customer", label: "Underskrevet af kunde" },
  { value: "approved_by_admin",  label: "Godkendt af admin" },
  { value: "active",             label: "Aktiv" },
  { value: "rejected",           label: "Afvist" },
  { value: "cancelled",          label: "Annulleret" },
];

function PaymentMethodBlock({ data, patchFields, busy }) {
  const isAutoCollect = data.payment_method === "automatic_collection";
  // Local controlled state for text/number fields — commit on blur only
  // to avoid an API call (and modal-flicker) on every keystroke.
  const [local, setLocal] = React.useState({
    payment_terms_days: data.payment_terms_days ?? "",
    automatic_collection_provider: data.automatic_collection_provider || "",
    automatic_collection_reference: data.automatic_collection_reference || "",
  });
  React.useEffect(() => {
    setLocal({
      payment_terms_days: data.payment_terms_days ?? "",
      automatic_collection_provider: data.automatic_collection_provider || "",
      automatic_collection_reference: data.automatic_collection_reference || "",
    });
  }, [data.payment_terms_days, data.automatic_collection_provider, data.automatic_collection_reference]);

  const commitField = (key, raw) => {
    const cur = data[key] ?? (typeof data[key] === "number" ? "" : "");
    if (raw === cur) return; // no-op if unchanged
    if (key === "payment_terms_days") {
      patchFields({ payment_terms_days: raw === "" ? null : parseInt(raw, 10) });
    } else {
      patchFields({ [key]: raw || null });
    }
  };

  return (
    <div className="bg-emerald-50 border border-emerald-300 p-4 mb-5" data-testid="quote-payment-block">
      <div className="text-sm font-bold text-emerald-900 mb-3">Betalingsmetode &amp; vilkår</div>
      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <div>
          <label className="block text-xs text-stone-600 mb-1">Betalingsmetode</label>
          <select value={data.payment_method || ""}
            onChange={(e) => patchFields({ payment_method: e.target.value || null })}
            disabled={busy}
            className="w-full border border-stone-300 p-2 bg-white" data-testid="quote-payment-method">
            <option value="">— Vælg —</option>
            {PAYMENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-stone-600 mb-1">Fakturafrekvens</label>
          <select value={data.invoice_frequency || ""}
            onChange={(e) => patchFields({ invoice_frequency: e.target.value || null })}
            disabled={busy}
            className="w-full border border-stone-300 p-2 bg-white" data-testid="quote-invoice-freq">
            <option value="">— Auto fra metode —</option>
            {INVOICE_FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-stone-600 mb-1">Betalingsfrist (dage netto)</label>
          <input type="number" min={0} max={90} value={local.payment_terms_days}
            onChange={(e) => setLocal((s) => ({ ...s, payment_terms_days: e.target.value }))}
            onBlur={(e) => commitField("payment_terms_days", e.target.value)}
            disabled={busy}
            className="w-full border border-stone-300 p-2" data-testid="quote-payment-terms" />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 items-center">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!data.payment_method_approved}
            onChange={(e) => patchFields({ payment_method_approved: e.target.checked })}
            data-testid="quote-payment-approved" />
          <span><strong>Betalingsmetode godkendt</strong> (kræves før booking-gate åbner)</span>
        </label>
      </div>

      {isAutoCollect && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-300" data-testid="quote-autocollect-block">
          <div className="text-xs font-bold text-amber-900 mb-2">Automatisk opkrævning — manuelt mandat-flow</div>
          <div className="text-xs text-amber-900 mb-2">
            Ingen live Leverandørservice/PBS-integration endnu. Admin styrer status manuelt.
            Booking tillades kun når status = <code className="bg-amber-100 px-1">active</code>.
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!data.automatic_collection_enabled}
                onChange={(e) => patchFields({ automatic_collection_enabled: e.target.checked })}
                data-testid="quote-autocollect-enabled" />
              <span>Mandat ønsket / igangsat</span>
            </label>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Mandat-status</label>
              <select value={data.automatic_collection_agreement_status || "not_enabled"}
                onChange={(e) => patchFields({ automatic_collection_agreement_status: e.target.value })}
                disabled={busy}
                className="w-full border border-stone-300 p-2 bg-white" data-testid="quote-autocollect-status">
                {AUTO_COLLECT_STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Provider</label>
              <input value={local.automatic_collection_provider}
                onChange={(e) => setLocal((s) => ({ ...s, automatic_collection_provider: e.target.value }))}
                onBlur={(e) => commitField("automatic_collection_provider", e.target.value)}
                disabled={busy}
                placeholder="manual_mandate_status"
                className="w-full border border-stone-300 p-2" data-testid="quote-autocollect-provider" />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Reference (valgfri)</label>
              <input value={local.automatic_collection_reference}
                onChange={(e) => setLocal((s) => ({ ...s, automatic_collection_reference: e.target.value }))}
                onBlur={(e) => commitField("automatic_collection_reference", e.target.value)}
                disabled={busy}
                className="w-full border border-stone-300 p-2" data-testid="quote-autocollect-reference" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);
